import type { Dayjs } from "dayjs";
import { ApiError } from "@/core/api/types";
import { dayjs } from "@/core/utils/date";
import type { WorkbookExportOptions } from "@/payroll/ai-script/types";
import { WorkbookCellEditBatchError } from "@/payroll/ai-script/workbook-cell-edit-batcher";

export interface DownloadAiScriptWorkbookParams extends WorkbookExportOptions {
  runId: string;
  filename: string;
}

const WORKBOOK_EXPORT_FIRST_POLL_DELAY = 750;

const WORKBOOK_EXPORT_POLL_DELAY = 2500;

export const WORKBOOK_EXPORT_TIMEOUT = 4 * 60 * 1000;

export const WORKBOOK_EXPORT_POLL_RETRIES = 3;

export const WORKBOOK_EXPORT_TIMEOUT_MESSAGE =
  "This workbook is taking longer than expected to prepare. Please try again in a few minutes.";

export const aiScriptWorkbookExportKey = (
  businessId: string | null,
  runId: string | null,
  attempt: number,
) => ["ai-script-workbook-export", businessId, runId, attempt] as const;

export const hasWorkbookExportExpired = (startedAt: Dayjs) =>
  dayjs().diff(startedAt, "milliseconds") >= WORKBOOK_EXPORT_TIMEOUT;

const WORKBOOK_EXPORT_TERMINAL_STATUSES = [401, 403, 404, 422, 500];

export const isTerminalExportError = (error: unknown) =>
  error instanceof WorkbookCellEditBatchError ||
  (error instanceof ApiError &&
    WORKBOOK_EXPORT_TERMINAL_STATUSES.includes(error.status));

export const getWorkbookExportPollDelay = (
  completedPolls: number,
  elapsedMs: number,
) => {
  const delay =
    completedPolls <= 1
      ? WORKBOOK_EXPORT_FIRST_POLL_DELAY
      : WORKBOOK_EXPORT_POLL_DELAY;

  return Math.max(1, Math.min(delay, WORKBOOK_EXPORT_TIMEOUT - elapsedMs));
};

export interface WorkbookExportRequest extends DownloadAiScriptWorkbookParams {
  attempt: number;
  startedAt: Dayjs;
  businessId: string;
}

export const saveWorkbookFile = (file: Blob, filename: string) => {
  const blobUrl = URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
};
